// P1-019: Gacha / Agent summon — neon industrial design
import { useState, useRef } from 'react';
import { usePlayerStore } from '@/shared/stores/playerStore';
import { useAgentStore } from '../stores/agentStore';
import type { GachaResult } from '../stores/agentStore';
import { GACHA_SINGLE_COST, GACHA_TEN_COST, GACHA_PITY_THRESHOLD } from '../data/agent-configs';
import { typeAbbr, rarityLabel, rarityColor } from '../utils/agent-ui-helpers';
import { SummonAnimation } from './SummonAnimation';

export function GachaPanel() {
  const agi = usePlayerStore((s) => s.agi);
  const drawGacha = useAgentStore((s) => s.drawGacha);
  const pityCounter = useAgentStore((s) => s.pityCounter);

  const [results, setResults] = useState<GachaResult[]>([]);
  const [wishText, setWishText] = useState('');
  const [showAnimation, setShowAnimation] = useState(false);
  const animWishRef = useRef('');

  const canSingle = agi >= GACHA_SINGLE_COST;
  const canTen = agi >= GACHA_TEN_COST;

  const handlePull = (type: 'single' | 'ten') => {
    const result = drawGacha(type);
    if (result) {
      setResults(result);
      animWishRef.current = wishText;
      setWishText('');
      setShowAnimation(true);
    }
  };

  const pityDisplay = `${pityCounter}/${GACHA_PITY_THRESHOLD}`;

  return (
    <div className="gacha-panel">
      {showAnimation && results.length > 0 && (
        <SummonAnimation
          results={results}
          wishText={animWishRef.current}
          onComplete={() => setShowAnimation(false)}
          portraitUrls={new Map()}
          currentRevealIndex={0}
        />
      )}

      {/* Title */}
      <h2 className="gacha-panel__title">AGENT SUMMON</h2>
      <div className="gacha-panel__subtitle">// RECRUIT NEW AGENTS FOR YOUR TEAM</div>

      {/* Balance + pity row */}
      <div className="gacha-panel__info-row">
        <div className="gacha-panel__balance">
          <span className="gacha-panel__balance-icon">◈</span>
          <span className="gacha-panel__balance-val">{agi.toLocaleString()}</span>
          <span className="gacha-panel__balance-label">AGI</span>
        </div>
        <div className="gacha-panel__pity">
          <span className="gacha-panel__pity-label">PITY:</span>
          <span className="gacha-panel__pity-val">{pityDisplay}</span>
        </div>
      </div>

      {/* Wish input */}
      <div className="gacha-panel__wish-row">
        <input
          className="gacha-panel__wish-input"
          type="text"
          maxLength={80}
          placeholder="输入你的召唤愿望..."
          value={wishText}
          onChange={(e) => setWishText(e.target.value)}
        />
        <span className="gacha-panel__wish-count">{wishText.length}/80</span>
      </div>

      {/* Pull buttons */}
      <div className="gacha-panel__buttons">
        <div>
          <button
            className="gacha-panel__pull-btn gacha-panel__pull-btn--single"
            disabled={!canSingle}
            onClick={() => handlePull('single')}
          >
            <span className="gacha-panel__pull-label">SINGLE SUMMON</span>
            <span className="gacha-panel__pull-cost">◈ {GACHA_SINGLE_COST}</span>
          </button>
          {!canSingle && (
            <span className="gacha-panel__deficit">还需 ◈{GACHA_SINGLE_COST - agi}</span>
          )}
        </div>
        <div>
          <button
            className="gacha-panel__pull-btn gacha-panel__pull-btn--ten"
            disabled={!canTen}
            onClick={() => handlePull('ten')}
          >
            <span className="gacha-panel__pull-label">10x SUMMON</span>
            <span className="gacha-panel__pull-cost">◈ {GACHA_TEN_COST}</span>
          </button>
          {!canTen && (
            <span className="gacha-panel__deficit">还需 ◈{GACHA_TEN_COST - agi}</span>
          )}
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <>
          <div className="gacha-panel__results-header">LAST SUMMON RESULTS</div>
          <div className="gacha-panel__results">
            {results.map((r, idx) => (
              <div
                key={idx}
                className="gacha-panel__result-card"
                style={{
                  borderColor: rarityColor(r.agent.rarity),
                  animationDelay: `${idx * 0.08}s`,
                }}
              >
                <span
                  className="gacha-panel__result-abbr"
                  style={{ color: rarityColor(r.agent.rarity) }}
                >
                  {typeAbbr(r.agent.type)}
                </span>
                <span className="gacha-panel__result-rarity">
                  {rarityLabel(r.agent.rarity)}
                </span>
                <span className="gacha-panel__result-name" title={r.agent.name}>
                  {r.agent.name.split('-')[0]}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
